// External Imports
import {
  BodyText,
  Button,
  IconEnvelopeSimpleBold,
  Inline,
  Stack,
} from '@lessonnine/design-system.lib'
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
// Internal Imports
import { trackGuiInteracted } from 'src/dataLayer/remoteData/babbelEventStream/index.js'
import { CaptchaMagicLink } from 'src/viewLayer/components/CaptchaMagicLink/CaptchaMagicLink.js'
import isEmailValid from 'src/viewLayer/components/flows/EmailLoginFlow/isEmailValid.js'
import useMagicLinkRequest from 'src/viewLayer/components/hooks/useMagicLinkRequest.js'
import { t } from 'src/viewLayer/helpers/translations.js'
import { useEmailPagePath } from 'src/viewLayer/navigation/index.js'

import { Container, Separator, SeparatorText } from './index.styled.js'
import { SuccessModal } from './SuccessModal.js'
// Component Definition
function MagicLink({ email, isPasswordLoginInProgress, setIsMagicLinkInProgress }) {
  const navigate = useNavigate()
  const isEmailCorrect = isEmailValid(email)
  const [isOpen, setIsOpen] = useState(false)
  const [requestFailed, setRequestFailed] = useState('')
  const [isButtonClicked, setIsButtonClicked] = useState(false)
  const [captchaToken, setCaptchaToken] = useState()
  const { isMagicLinkRequestInProgress, makeMagicLinkRequest } = useMagicLinkRequest({
    captchaResponseToken: captchaToken,
    email,
  })
  const isMagicLinkInProgress = isMagicLinkRequestInProgress || isButtonClicked
  const emailPagePath = useEmailPagePath()

  useEffect(() => {
    setIsMagicLinkInProgress?.(isMagicLinkInProgress)
  }, [isMagicLinkInProgress, setIsMagicLinkInProgress])

  const handleMagicLinkClick = () => {
    setIsButtonClicked(true)
    setRequestFailed('')
  }

  useEffect(() => {
    if (!captchaToken || !isButtonClicked) {
      return
    }
    trackGuiInteracted({ guiElementName: 'magic_link_click' })
    setIsButtonClicked(false)
    ;(async () => {
      const result = await makeMagicLinkRequest()
      if (result?.error) {
        setRequestFailed(result.error)
        return
      }
      setIsOpen(true)
    })()
  }, [captchaToken, isButtonClicked, makeMagicLinkRequest])

  const hanndleCloseModal = () => {
    setIsOpen(false)
    navigate(emailPagePath, { replace: true })
  }

  return (
    <>
      <Container
        padding={{
          small: [40, 0, 4],
          xxsmall: [32, 0, 4],
        }}
      >
        <Inline align="center" alignItems="center">
          <SeparatorText>
            <Separator data-content={t('login.magic_link.alternative_login')} />
          </SeparatorText>
        </Inline>
        <Stack align="center" gap={8} padding={[32, 0, 0, 0]}>
          <BodyText size="small" textAlign="center" weight="strong">
            {t('login.magic_link.explanation_header')}{' '}
          </BodyText>
          <BodyText size="small" textAlign="center">
            {t('login.magic_link.explanation')}
          </BodyText>
          <Button
            color="secondary-emphasis"
            data-testid="magic-link-button"
            disabled={isMagicLinkInProgress || !isEmailCorrect || isPasswordLoginInProgress}
            fullWidth
            icon={<IconEnvelopeSimpleBold />}
            loading={isMagicLinkInProgress}
            onClick={handleMagicLinkClick}
          >
            {t('login.magic_link.cta_button')}
          </Button>
          {requestFailed && (
            <BodyText
              as="p"
              color={tokens.color.theme.dark.reference.red.red60}
              size="small"
              textAlign="center"
            >
              {t(requestFailed)}
            </BodyText>
          )}
        </Stack>
      </Container>
      <SuccessModal email={email} handleCloseModal={hanndleCloseModal} isOpen={isOpen} />
      <CaptchaMagicLink setCaptchaToken={setCaptchaToken} shouldExecuteCaptcha={isButtonClicked} />
    </>
  )
}

export { MagicLink }
